import React, { useState, useEffect } from 'react';

import { useForm } from 'react-hook-form';
import { Auth } from 'aws-amplify';

import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';

import CaseName from './CaseName';
import Directories from './Directories';
import NotLinkedToCase from './NotLinkedToCase';
import UploadInput from './UploadInput';
import FilesList from './FilesList';
import ContactDropdown from './ContactDropdown';

import { useToast } from 'context/ToastContext';
import { useFileManagerContext } from 'modules/FileManagerV2/context/FileManagerContext';

import { useFileMutations } from 'modules/FileManagerV2/hooks/api/useFileMutations';
import { toastConstant } from 'constants/toastmessage';
import { useNavbarContext } from 'context/NavbarContext';
import { inferMimeTypeFromName } from 'modules/FileManagerV2/utils/file/mimeFallback';

function FileUploadModal() {
  const {
    modals,
    caseDetails,
    fileManagerFilters,
    selectedItem,
    isGlobalFileManager,
    isCaseFileManager,
    isLeadFileManager,
    isClientFileManager,
    onMountUnMount,
    onOperationBreak,
    versionFileUpload,
    breadCrumbs,
  } = useFileManagerContext();
  const { showFileUploadModal, setShowFileUploadModal, isFirmDocumentsUpload, setIsFirmDocumentsUpload } = modals;
  const [uploadingIndex, setUploadingIndex] = useState('');

  const { startUpload } = useNavbarContext();

  const {
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    control,
    resetField,
  } = useForm();

  const notLinkedToCaseWatcher = watch('not_linked_to_case');
  const filesWatcher = watch('files');

  const { addToast } = useToast();
  const { createFile, updateFile } = useFileMutations();

  const handleCloseModal = () => {
    setShowFileUploadModal(false);
    setIsFirmDocumentsUpload(false);
  };

  //Automated Time entires logic
  useEffect(() => {
    const caseId = caseDetails?.case_id || fileManagerFilters?.case?.id;
    typeof onMountUnMount === 'function' && onMountUnMount('upload-file', 'mounted', caseId);
  }, []);

  // Set default value for not_linked_to_case when uploading to firm documents
  useEffect(() => {
    if (isFirmDocumentsUpload) {
      setValue('not_linked_to_case', true);
    }
  }, [isFirmDocumentsUpload, setValue]);

  async function uploadFiles(data) {
    try {
      // For firm documents upload, ensure not_linked_to_case is true
      if (isFirmDocumentsUpload) {
        data.not_linked_to_case = true;
      }
      const caseId = caseDetails?.case_id || fileManagerFilters?.case?.id;
      const folderId =
        selectedItem?.sk || selectedItem?.value || breadCrumbs?.[breadCrumbs.length - 1]?.value || fileManagerFilters?.selectedFolder;
      const folderPath = breadCrumbs?.length > 0 ? breadCrumbs.map((b) => b.value).filter(Boolean) : undefined;

      const onComplete = async (files = []) => {
        if (files?.length === 0) return;
        let attachments = [];
        let user = await Auth.currentAuthenticatedUser();

        files.forEach((file) => {
          const version_id = file?.VersionId || '';
          if (version_id) {
            const file_details = {
              file_name: file?.file.name,
              display_name: file?.file.name,
              size: file?.file?.size.toString(),
              file_path: file?.Key,
              file_type: file?.file?.type?.toString() || inferMimeTypeFromName(file?.file?.name),
              created_by: user.attributes,
              version_id: version_id,
              is_court_setting_notice: file?.file?.is_court_setting_notice,
            };

            if (selectedItem) {
              if (!selectedItem.versions) {
                selectedItem.versions = [];
              }
              selectedItem.versions.push({
                file_name: selectedItem.file_name,
                display_name: selectedItem.display_name,
                size: selectedItem.size,
                file_path: selectedItem.file_path,
                file_type: selectedItem.file_type,
                created_by: selectedItem.user?.attributes,
                version_id: selectedItem.version_id,
                version_edited: new Date().toString(),
                is_court_setting_notice: selectedItem?.is_court_setting_notice,
              });

              selectedItem.file_name = file?.file?.name;
              selectedItem.display_name = file?.file?.name;
              selectedItem.version_id = version_id;
              selectedItem.created_by = user.attributes;
              selectedItem.file_type = file?.file?.type?.toString() || inferMimeTypeFromName(file?.file?.name);
              selectedItem.file_path = file?.Key;
              selectedItem.size = file?.file?.size.toString();
              selectedItem.is_court_setting_notice = file?.file?.is_court_setting_notice;
            } else {
              attachments.push({
                ...file_details,
                versions: [],
                complete_path: (file?.file?.webkitRelativePath || file?.file?.path || file?.file?.name)?.replace(/^\/+/, ''),
              });
            }
          }
        });

        if (selectedItem) {
          updateFileHandler(data, versionFileUpload ? selectedItem : attachments);
        } else {
          createFileHandler(data, attachments);
        }
      };

      startUpload(data?.files || [], undefined, onComplete, { caseId, folderId, folderPath, isGlobalUpload: isFirmDocumentsUpload });

      handleCloseModal();
    } catch (error) {
      if (error?.message === 'Network Error') {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, toastConstant.message.NETWORK_ERROR);
        return;
      }
      addToast(
        false,
        toastConstant.toasterType.ERROR,
        toastConstant.api.ERROR,
        error?.response?.status === 403 ? error.response?.data : toastConstant.message.FILE_UPLOAD_FAILED
      );
      //Automated Time entires logic
      isCaseFileManager && typeof onOperationBreak === 'function' && onOperationBreak();
    }
  }

  const createFileHandler = async (data, attachments) => {
    let case_id;

    if (data && data.case && data.case.id) {
      case_id = data.case.id;
    } else if (isClientFileManager) {
      case_id = fileManagerFilters.case;
    } else if (caseDetails && caseDetails.case_id !== undefined) {
      case_id = caseDetails.case_id;
    } else {
      case_id = 'undefined';
    }

    const dataObj = {
      case_id: case_id,
      type: 'bulk_upload',
      folder_id: breadCrumbs ? breadCrumbs[breadCrumbs?.length - 1]?.value : fileManagerFilters.selectedFolder,
      file_folder: attachments,
      not_linked_to_case: data.not_linked_to_case,
      is_lead: isLeadFileManager ? isLeadFileManager : data?.case?.is_lead,
      // Include shared contacts if selected
      ...(data.sharedContacts &&
        data.sharedContacts.length > 0 && {
          client_cognito_username: data.sharedContacts,
        }),
    };

    const obj = {
      data: dataObj,
      is_lead: isLeadFileManager ? isLeadFileManager : data?.case?.is_lead,
    };

    try {
      await createFile(obj);
      handleCloseModal();
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.FILES_UPLOADED);
      //Automated Time entires logic
      if (!isLeadFileManager && case_id && case_id !== 'undefined') {
        typeof onMountUnMount === 'function' && onMountUnMount('upload-file', 'unmounted', case_id);
      } else {
        typeof onOperationBreak === 'function' && onOperationBreak();
      }
    } catch (error) {
      const errMsg = typeof error?.response?.data === 'string' ? error?.response?.data : '';
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, errMsg || toastConstant.message.FILE_UPLOAD_FAILED);
    }
    setUploadingIndex('');
  };

  const updateFileHandler = async (data, updatedData) => {
    let case_id;

    if (data && data.case && data.case.id) {
      case_id = data.case.id;
    } else if (isClientFileManager) {
      case_id = fileManagerFilters.case;
    } else if (caseDetails && caseDetails.case_id !== undefined) {
      case_id = caseDetails.case_id;
    } else {
      case_id = 'undefined';
    }

    updatedData.case_id = case_id;

    const obj = {
      data: updatedData,
      is_lead: isLeadFileManager ? isLeadFileManager : data?.case?.is_lead,
    };

    try {
      await updateFile(obj);
      handleCloseModal();
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.FILES_UPLOADED);
      //Automated Time entires logic
      if (!isLeadFileManager && case_id && case_id !== 'undefined') {
        typeof onMountUnMount === 'function' && onMountUnMount('upload-file', 'unmounted', case_id);
      } else {
        typeof onOperationBreak === 'function' && onOperationBreak();
      }
    } catch (error) {
      console.log(error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, toastConstant.message.FILE_UPLOAD_FAILED);
    }
    setUploadingIndex('');
  };

  return (
    <div className="file-upload-sidebar">
      <Sidebar
        visible={showFileUploadModal}
        style={{ width: '40em' }}
        onHide={handleCloseModal}
        className="file-manager-sidebar z-max"
        position="right"
      >
        <div className="file-upload-header d-flex justify-content-between align-items-center py-2 my-3">
          <div className="pointer">
            <i onClick={handleCloseModal} className="icon-back me-2 p-2"></i>
            <label className="sub-title">Upload</label>
          </div>
        </div>
        <br />

        <div className="file-upload-container d-flex flex-column justify-content-between">
          {(isGlobalFileManager || isClientFileManager) && !isFirmDocumentsUpload && (
            <CaseName control={control} notLinkedToCaseWatcher={notLinkedToCaseWatcher} errors={errors} setValue={setValue} />
          )}

          {!isFirmDocumentsUpload && <Directories />}

          {isGlobalFileManager && !fileManagerFilters.case && !isFirmDocumentsUpload && (
            <NotLinkedToCase control={control} errors={errors} resetField={resetField} />
          )}

          <UploadInput control={control} errors={errors} />

          {/* Show contact dropdown only when opened from case details */}
          {(isCaseFileManager || isLeadFileManager) && caseDetails?.case_id && (
            <ContactDropdown
              control={control}
              errors={errors}
              caseId={caseDetails.case_id}
              isVisible={(isCaseFileManager || isLeadFileManager) && !!caseDetails?.case_id}
            />
          )}

          <div className="d-flex justify-content-end mt-3">
            <Button label="Cancel" className="p-button  p-button-secondary outline ms-2" onClick={handleCloseModal} />{' '}
            <Button label="Upload" className="p-button p-button-secondary ms-2" onClick={handleSubmit(uploadFiles)} />
          </div>
        </div>
        <br />

        <FilesList
          filesWatcher={filesWatcher}
          uploadingIndex={uploadingIndex}
          setValue={setValue}
          isClientFileManager={isClientFileManager}
        />
      </Sidebar>
    </div>
  );
}

export default FileUploadModal;
